import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShoppingBag, Menu } from 'lucide-react';
import { useCart } from '../../lib/cart';
import { useLanguage } from '../../lib/i18n';
import { LanguageSelector } from '../ui/LanguageSelector';
interface MarketplaceHeaderProps {
  showPromo?: boolean;
}
export function MarketplaceHeader({
  showPromo = false
}: MarketplaceHeaderProps) {
  const {
    getItemCount,
    cart
  } = useCart();
  const {
    t
  } = useLanguage();
  const {
    slug: urlSlug
  } = useParams<{
    slug: string;
  }>();
  const itemCount = getItemCount();
  // Fallback to URL slug if cart doesn't have it (for legacy carts)
  const targetSlug = cart.restaurantSlug || urlSlug;
  // ── Emilius "white-label" mode ──────────────────────────────────────
  // El dueño quiere que dentro del flujo del restaurante (menú, carrito,
  // checkout, confirmación, tracking) NO existan caminos hacia el
  // marketplace de NOIZA. Detectamos por URL o por el slug guardado en
  // el carrito (cubre las páginas /cart, /checkout, /order/..., etc.
  // donde el slug no está en la URL).
  const isEmiliusFlow = urlSlug === 'emilius-pizza' || cart.restaurantSlug === 'emilius-pizza';
  if (isEmiliusFlow) {
    return <header className="sticky top-0 z-40 w-full bg-[#0B0B0B] border-b border-white/10" data-id="element-1912">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8" data-id="element-1913">
          <div className="flex justify-between items-center h-12 sm:h-16" data-id="element-1914">
            {/* Emilius mark — intentionally NOT a link. The whole point
                of this mode is to leave the restaurant only via the
                cart/checkout flow, never back to the marketplace. */}
            <div className="flex items-center gap-2 select-none" data-id="element-1915">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg overflow-hidden bg-[#F2E6D5] flex items-center justify-center shrink-0" data-id="element-1916">
                <img src="https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png" alt="Emilius Pizza" className="w-full h-full object-contain" decoding="async" data-id="element-1917" />
              </div>
              <span className="font-bold text-sm sm:text-base tracking-tight text-white" data-id="element-1918">
                Emilius Pizza
              </span>
            </div>

            {/* Right side: powered-by label (plain text, NOT a link),
                language selector, and the customer's cart. The cart
                link stays — it goes to the user's own cart, not to
                the marketplace. */}
            <div className="flex items-center gap-2 sm:gap-4" data-id="element-1919">
              <span className="hidden sm:inline text-[10px] tracking-wide text-white/35 select-none" aria-label="Powered by noizacr.com" data-id="element-1920">
                Powered by noizacr.com
              </span>

              <LanguageSelector data-id="element-1921" />

              <Link to={targetSlug ? `/r/${targetSlug}/cart` : '/cart'} className="relative p-1.5 sm:p-2 text-white/60 hover:text-white transition-colors rounded-full" data-id="element-1922">
                <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1923" />
                {itemCount > 0 && <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] sm:text-xs font-bold leading-none text-[#0B0B0B] transform translate-x-1/4 -translate-y-1/4 bg-[#6FA248] rounded-full min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-[20px]" data-id="element-1924">
                    {itemCount}
                  </span>}
              </Link>
            </div>
          </div>

          {/* Mobile: powered-by sits under the bar so the tiny text is
                still visible without crowding the right-side actions. */}
          <div className="sm:hidden pb-1 -mt-0.5 text-center" data-id="element-1925">
            <span className="text-[9px] tracking-wide text-white/30 select-none" data-id="element-1926">
              Powered by noizacr.com
            </span>
          </div>
        </div>
      </header>;
  }
  // ── Default NOIZA marketplace header (unchanged for all other tenants) ──
  return <header className="sticky top-0 z-40 w-full bg-[#0B0B0B] border-b border-white/10" data-id="element-1927">
      {/* Promo strip — only on marketplace/home */}
      {showPromo && <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-600 text-white text-center py-1 sm:py-1.5 px-2 sm:px-4" data-id="element-1928">
          <Link to="/r/capricho-de-canela" className="text-[11px] sm:text-sm font-bold hover:underline block truncate" data-id="element-1929">
            🎉 Capricho de Canela: ¡2do producto a mitad de precio! →
          </Link>
        </div>}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8" data-id="element-1930">
        <div className="flex justify-between items-center h-12 sm:h-16" data-id="element-1931">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-id="element-1932">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#C9A34E] rounded-lg flex items-center justify-center" data-id="element-1933">
              <span className="text-[#0B0B0B] font-bold text-base sm:text-lg leading-none" data-id="element-1934">
                N
              </span>
            </div>
            <span className="font-bold text-lg sm:text-xl tracking-tight text-white" data-id="element-1935">
              NOIZA
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-1936">
            <Link to="/" className="text-sm font-medium text-white/60 hover:text-[#C9A34E] transition-colors" data-id="element-1937">
              {t('home')}
            </Link>
            <Link to="/restaurants" className="text-sm font-medium text-white/60 hover:text-[#C9A34E] transition-colors" data-id="element-1938">
              {t('restaurants')}
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-4" data-id="element-1939">
            <LanguageSelector data-id="element-1940" />

            <Link to={targetSlug ? `/r/${targetSlug}/cart` : '/cart'} className="relative p-1.5 sm:p-2 text-white/60 hover:text-white transition-colors rounded-full" data-id="element-1941">
              <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1942" />
              {itemCount > 0 && <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] sm:text-xs font-bold leading-none text-[#0B0B0B] transform translate-x-1/4 -translate-y-1/4 bg-[#C9A34E] rounded-full min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-[20px]" data-id="element-1943">
                  {itemCount}
                </span>}
            </Link>

            <button className="md:hidden p-1.5 text-white/60 hover:text-white rounded-full" data-id="element-1944">
              <Menu className="w-5 h-5" data-id="element-1945" />
            </button>
          </div>
        </div>
      </div>
    </header>;
}